import { useMutation } from '@apollo/client';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Select } from '@zoopla-eng/zds-components/select';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { CardTitle } from '@zoopla-eng/zds-components/headings';
import { LockSmall } from '@zoopla-eng/zds-components/icons';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Bulleted } from '@zoopla-eng/zds-components/lists/bulleted';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { CREATE_SAVED_SEARCH } from '@zoopla/listing-search-queries';
import {
    getForSaleFilters,
    getPolyenc,
    getQ,
    getRadius,
    getSearchPath,
    getSearchType,
    getToRentFilters,
    getDuration,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { ReactElement, RefObject, useEffect, useState } from 'react';
import time from './assets/time.svg';
import {
    selectWrapperStyle,
    styledSearchPathStyle,
    styledSmallLabelsStyle
} from './SaveThisSearchModal.styles.css';
import {
    formatAdded,
    formatAvailableFrom,
    formatBedrooms,
    formatBillsIncluded,
    formatBuyerIncentive,
    formatChainFree,
    formatFeature,
    formatFurnishedState,
    formatHomeType,
    formatIncludeRented,
    formatIncludeSharedAccommodation,
    formatIncludeSold,
    formatKeywords,
    formatLocation,
    formatPetsAllowed,
    formatPrice,
    formatPropertyTypes,
    formatRadius,
    formatReducedPriceOnly,
    formatRentalTerm,
    formatSearchPath,
    formatSearchType
} from './utils';

enum ModalState {
    INITIAL = 'initial',
    SUCCESS = 'success',
    ERROR = 'error'
}

const freqOptions = [
    {
        id: '0',
        text: 'Instant property alerts'
    },
    {
        id: '1',
        text: 'Daily summary emails'
    },
    {
        id: '3',
        text: 'Summary every 3 days'
    },
    {
        id: '7',
        text: 'Weekly summary emails'
    },
    {
        id: '',
        text: 'No email alerts'
    }
];

export type SaveThisSearchModalProps = {
    defaultAlertFrequency?: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    container?: RefObject<HTMLDivElement>;
};

export const SaveThisSearchModal = ({
    defaultAlertFrequency = '',
    isOpen,
    onOpenChange,
    container
}: SaveThisSearchModalProps) => {
    const [modalState, setModalState] = useState<
        ModalState.INITIAL | ModalState.SUCCESS | ModalState.ERROR
    >(ModalState.INITIAL);

    const { query, pathname } = useRouter();

    const polyenc = getPolyenc(query);
    const q = getQ(query);
    const radius = getRadius(query);
    const searchPath = getSearchPath(query);
    const searchType = getSearchType(pathname);
    const duration = getDuration(query);
    const transportType = getTransportType(query);

    const formattedRadius = formatRadius(radius);
    const formattedSearchPath = formatSearchPath(searchPath);
    const forSaleFilters = getForSaleFilters(query, pathname);
    const toRentFilters = getToRentFilters(query);

    const allFilters = {
        ...forSaleFilters,
        ...toRentFilters
    };

    const {
        added,
        available_from: availableFrom,
        beds_max: bedsMax,
        beds_min: bedsMin,
        bills_included: billsIncluded,
        buyer_incentive: buyerIncentive,
        chain_free: chainFree,
        feature,
        furnished_state: furnishedState,
        include_rented: includeRented,
        include_shared_accommodation: includeSharedAccommodation,
        include_sold: includeSold,
        is_auction: isAuction,
        is_retirement_home: isRetirementHome,
        is_shared_ownership: isSharedOwnership,
        keywords,
        new_homes: newHomes,
        pets_allowed: petsAllowed,
        price_frequency: priceFrequency,
        price_max: priceMax,
        price_min: priceMin,
        property_sub_type: propertySubType,
        property_type: propertyType,
        reduced_price_only: reducedPriceOnly,
        rental_term: rentalTerm
    } = allFilters;

    const formattedFilters = [
        formatAdded(added),
        formatAvailableFrom(availableFrom),
        formatBedrooms(bedsMax, bedsMin),
        formatBillsIncluded(billsIncluded),
        formatBuyerIncentive(buyerIncentive),
        formatChainFree(chainFree),
        formatFeature(feature),
        formatFurnishedState(furnishedState),
        ...formatHomeType(
            isAuction,
            isRetirementHome,
            isSharedOwnership,
            newHomes
        ),
        formatIncludeRented(includeRented),
        formatIncludeSharedAccommodation(includeSharedAccommodation),
        formatIncludeSold(includeSold),
        formatKeywords(keywords),
        formatPetsAllowed(petsAllowed),
        formatPrice(priceMax, priceMin),
        formatPropertyTypes(propertySubType),
        formatReducedPriceOnly(reducedPriceOnly),
        formatRentalTerm(rentalTerm)
    ].reduce<ReactElement[]>((els, formattedFilter) => {
        if (!formattedFilter) {
            return els;
        }

        // eslint-disable-next-line react/jsx-no-useless-fragment
        return [...els, <>{formattedFilter}</>];
    }, []);

    const formattedSearchType = formatSearchType(searchType);

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            if (data.createNewSavedSearch.status === 200) {
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'search results',
                    eventAction: 'success',
                    eventLabel:
                        defaultAlertFrequency === ''
                            ? 'save_search'
                            : 'create_alert'
                });

                setModalState(ModalState.SUCCESS);
            } else {
                setModalState(ModalState.ERROR);
            }
        },
        onError: (e) => {
            setModalState(ModalState.ERROR);
        }
    });

    const defaultSearchName = q ? q : formattedSearchPath;
    const [searchName, setSearchName] = useState(defaultSearchName);

    useEffect(() => {
        setSearchName(defaultSearchName);
    }, [defaultSearchName]);

    const [alertFrequency, setAlertFrequency] = useState(defaultAlertFrequency);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        setAlertFrequency(defaultAlertFrequency);
    }, [defaultAlertFrequency]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        createSavedSearchMutation({
            variables: {
                input: {
                    added,
                    availableFrom,
                    bedsMax,
                    bedsMin,
                    billsIncluded,
                    buyerIncentive,
                    chainFree,
                    description: searchName,
                    duration,
                    feature,
                    frequency: alertFrequency,
                    furnishedState,
                    identifier: formatLocation(searchPath),
                    includeRented,
                    includeRetirementHomes:
                        isRetirementHome !== 'false' ? 'true' : 'false',
                    includeSharedAccommodation: includeSharedAccommodation
                        ? includeSharedAccommodation
                        : 'true',
                    includeSharedOwnership:
                        isSharedOwnership !== 'false' ? 'true' : 'false',
                    includeSold,
                    isAuction,
                    isRetirementHome,
                    isSharedOwnership,
                    keywords,
                    newHomes,
                    petsAllowed,
                    polyenc,
                    priceFrequency: priceFrequency
                        ? priceFrequency
                        : 'per_month',
                    priceMax,
                    priceMin,
                    propertySubType,
                    propertyType:
                        propertyType === 'property' ? '' : propertyType,
                    q: q ? q : formatLocation(searchPath),
                    radius: radius ? radius : '0',
                    reducedPriceOnly,
                    rentalTerm,
                    section: searchType,
                    transportType,
                    userAlertId: ''
                }
            }
        });
    };

    return (
        <ControlledDialog
            isOpen={isOpen}
            onClose={() => {
                setModalState(ModalState.INITIAL);

                onOpenChange(false);
            }}
            title="Save search and email alert"
            label="Save search and email alert"
            container={container?.current}
        >
            <>
                {modalState === ModalState.INITIAL ? (
                    <form onSubmit={onSubmit}>
                        <Stack>
                            <Box display="flex" justifyContent="center">
                                <img
                                    alt=""
                                    src={time}
                                    width="120px"
                                    height="120px"
                                />
                            </Box>
                            <CardTitle>
                                {`Save your search and create an alert ${formattedSearchType} ${formattedRadius}`}{' '}
                                <span className={styledSearchPathStyle}>
                                    {formattedSearchPath}
                                </span>
                            </CardTitle>
                            <Bulleted items={formattedFilters} />
                            <Divider direction="horizontal" />
                            <InputField
                                name="name-your-search"
                                label="Name your search"
                                value={searchName}
                                onChange={(e) => {
                                    setSearchName(e.target.value);
                                }}
                            />
                            <div className={selectWrapperStyle}>
                                <Select
                                    options={freqOptions}
                                    label="How often do you like to receive alerts?"
                                    onSelectedOptionChange={(v) =>
                                        setAlertFrequency(v.id)
                                    }
                                    selectedOption={
                                        freqOptions.find(
                                            (o) => o.id === alertFrequency
                                        ) ?? freqOptions[0]
                                    }
                                />
                            </div>
                            <Button category="primary" disabled={!searchName}>
                                Save your search and alert
                            </Button>
                        </Stack>
                    </form>
                ) : null}
                {modalState === ModalState.SUCCESS ? (
                    <Stack>
                        <CardTitle>
                            {`Success! Your search "${searchName}" has been saved`}
                        </CardTitle>
                        <p>
                            You can access your saved searches from MyZoopla
                            account.
                        </p>
                        <Button
                            category="primary"
                            onClick={() => {
                                setModalState(ModalState.INITIAL);

                                onOpenChange(false);
                            }}
                        >
                            Return to search results
                        </Button>
                        <Box display="flex" justifyContent="center">
                            <Link href="/myaccount/alerts-searches/">
                                Manage my saved searches
                            </Link>
                        </Box>
                        <Box
                            display="flex"
                            columnGap="S200"
                            alignItems="center"
                        >
                            <LockSmall />
                            <p
                                className={`${styledSmallLabelsStyle} ${fontGroups.fork}`}
                            >
                                We will not share your details with third
                                parties
                            </p>
                        </Box>
                    </Stack>
                ) : null}
                {modalState === ModalState.ERROR ? (
                    <div className="error-message">
                        Sorry, we were unable to save your search or create an
                        alert at this time. Please try again later or consider
                        resetting your filters.
                    </div>
                ) : null}
            </>
        </ControlledDialog>
    );
};
